import { Ref } from "vue";

import {
  Calendar,
  CalendarResourceWithType,
  PersonWithType,
  RoomWithType,
} from "frontend/interfaces/calendar";
import { DayViewRaw } from "frontend/interfaces/day-view";
import { parseUnit } from "frontend/parser/parse-unit";
import { parsePerson } from "frontend/parser/parse-person";
import { Person } from "frontend/interfaces/person";
import { header } from "frontend/uses/abstract-view/keys-and-labels";
import { useAbstractViewParser } from "frontend/uses/abstract-view/use-abstract-view-parser";
import { useStore as useProfileStore } from "frontend/stores/profile";
import { parseRoom } from "frontend/parser/parse-room";
import { Room } from "frontend/interfaces/room";
import { parseHoliday } from "frontend/parser/settings/parse-holiday";
import { parseNote } from "frontend/parser/parse-note";
import {
  holiday,
  isNotePartOfPerson,
  note,
  onClickCreateNote,
} from "frontend/uses/abstract-view/events";
import { DayPart } from "shared/utils/date-utils";

export function useDayViewParser(
  date: Date,
  dayView: DayViewRaw,
  dayPart?: Ref<DayPart | null>
): Calendar {
  const { sortPersons, filterPersons, sortRooms, filterRooms } =
    useProfileStore();

  const pivotPersonSelector = (persons: Person[]): PersonWithType[] => {
    return filterPersons(sortPersons(persons)).map((p) => ({
      ...p,
      type: "person",
    }));
  };

  const pivotRoomSelector = (rooms: Room[]): RoomWithType[] => {
    return filterRooms(sortRooms(rooms)).map((r) => ({
      ...r,
      type: "room",
    }));
  };

  return useAbstractViewParser<CalendarResourceWithType>(
    {
      units: dayView.units.map((unitRaw) => parseUnit(unitRaw, date)),
      persons: dayView.persons.map(parsePerson),
      rooms: dayView.rooms.map(parseRoom),
      dates: [date],
    },
    {
      calculateColumnPivots: (database) => {
        const persons = pivotPersonSelector(database.persons);
        const rooms = pivotRoomSelector(database.rooms);
        return [...persons, ...rooms];
      },
      calculateColumnHeader: (pivot, _database, _options) =>
        header(pivot.id, pivot.name),
      calculateColumnEvent: (pivot, database, _options) => {
        if (pivot.type !== "person") return {};
        return {
          onClick: onClickCreateNote(database, pivot.id, date),
        };
      },
      calculateEvents: (database, _options) => {
        const persons = pivotPersonSelector(database.persons);
        const holidays = dayView.holidays
          .map(parseHoliday)
          .flatMap((h) =>
            persons.map((person, column) => holiday(h, person.id, [column]))
          );
        const notes = dayView.notes
          .map(parseNote)
          .flatMap((n) =>
            persons.flatMap((person, column) =>
              isNotePartOfPerson(n, person.id)
                ? [note(database, n, person.id, [column])]
                : []
            )
          );
        return [...holidays, ...notes];
      },
      calculateCellAt: (
        resource,
        intervalReferenceDate,
        columnIntervalReferenceDate,
        _options
      ) => ({
        resource: resource,
        interval: intervalReferenceDate,
        columnInterval: columnIntervalReferenceDate,
        key: resource.id,
      }),
    },
    { dayPart }
  );
}
